import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import teamAPI from "../../../services/teamApi";
import { DOCTORS_BASE } from "../AdminDoctors/doctorConstants";
import { loadUpcomingTrainerSessions } from "../../../utils/trainerSessionUtils";
import { TEAM_BASE, TEAM_ROLE_LABELS } from "./teamConstants";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import L from "../../../constants/baysportLabels";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import SwipeableRow from "../AdminPortal/SwipeableRow";
import {
  HubListGroup,
  HubNavAdd,
  HubRow,
  HubScreen,
  HubSegment,
  HubSheet,
  HubSheetActions,
  DesktopDialog,
} from "../AdminPortal/HubPwa";
import TeamInviteForm from "./TeamInviteForm";
import s from "../clinic.module.css";
import u from "../../ui/ui.module.css";
import {
  Button,
  Input,
  Select,
  EmptyState,
  useToast,
  IconPlus,
  IconSearch,
} from "../../ui";

function roleBadgeClass(role) {
  if (role === "owner") return u.badgePurple;
  if (role === "admin") return u.badgeBlue;
  return u.badgeGreen;
}

function formatAssignments(member) {
  if (member.role !== "trainer") {
    if (member.role === "admin" || member.role === "owner") return "Полный доступ";
    return "—";
  }
  const groupNames = (member.groups || []).map((g) => g.name).filter(Boolean);
  if (groupNames.length) return groupNames.join(", ");
  const branchNames = (member.branches || []).map((b) => b.name);
  if (branchNames.length) return `залы: ${branchNames.join(", ")}`;
  return "Группа не назначена";
}

function memberCanManage(member, currentUserId) {
  return member.id !== currentUserId && !member.is_owner;
}

function memberStatus(member) {
  if (member.is_blocked) return { label: "Заблокирован", tone: "danger" };
  if (member.trainer_id && member.trainer_active === false) {
    return { label: "Неактивен", tone: "danger" };
  }
  return { label: "Активен", tone: "ok" };
}

function TeamActions({ member, onResend, onToggleBlock, busyId, currentUserId, onProfile, onSchedule, layout = "sheet" }) {
  const isSelf = member.id === currentUserId;
  const canManage = !isSelf && !member.is_owner;
  const showProfile = member.role === "trainer" && member.trainer_id;
  const isTable = layout === "table";
  const compact = layout === "compact";

  const btnClass = compact ? aa.teamRowAction : undefined;
  const Btn = compact ? "button" : Button;
  const btnProps = compact
    ? { type: "button", className: btnClass, disabled: busyId === member.id }
    : {
        type: "button",
        size: "sm",
        variant: isTable ? "secondary" : "secondary",
        disabled: busyId === member.id,
      };

  const items = [];

  if (canManage) {
    items.push(
      <Btn key="invite" {...btnProps} onClick={() => onResend(member.id)}>
        {compact || isTable ? "Приглашение" : "Отправить приглашение"}
      </Btn>,
      <Btn key="block" {...btnProps} onClick={() => onToggleBlock(member)}>
        {member.is_blocked
          ? compact || isTable
            ? "Разблок."
            : "Разблокировать доступ"
          : compact || isTable
            ? "Блок."
            : "Заблокировать доступ"}
      </Btn>,
    );
  }

  if (showProfile) {
    items.push(
      <Btn
        key="profile"
        {...btnProps}
        onClick={() => onProfile(member.trainer_id)}
      >
        {compact || isTable ? "Профиль" : "Открыть профиль"}
      </Btn>,
      <Btn key="schedule" {...btnProps} onClick={() => onSchedule(member.trainer_id)}>
        {compact || isTable ? "Расписание" : "Открыть расписание"}
      </Btn>,
    );
  }

  if (!items.length) {
    if (compact) return null;
    return <span className={s.teamTableEmpty}>—</span>;
  }

  if (isTable) {
    return <div className={md.desktopTeamTableActions}>{items}</div>;
  }

  return compact ? (
    <div className={aa.teamRowActions}>{items}</div>
  ) : (
    <div className={aa.teamMemberSheetActions}>{items}</div>
  );
}

function TeamTableRow({ member, onResend, onToggleBlock, busyId, currentUserId, onProfile, onSchedule }) {
  const roleLabel = TEAM_ROLE_LABELS[member.role] || member.role;
  const detail = formatAssignments(member);
  const status = memberStatus(member);
  const showProfileLink = member.role === "trainer" && member.trainer_id;

  return (
    <tr className={member.is_blocked ? s.teamTableRowBlocked : undefined}>
      <td className={s.teamTableCell}>
        {showProfileLink ? (
          <button
            type="button"
            className={md.desktopTeamNameLink}
            onClick={() => onProfile(member.trainer_id)}
          >
            {member.full_name || "—"}
          </button>
        ) : (
          <span className={s.teamMemberName}>{member.full_name || "—"}</span>
        )}
      </td>
      <td className={s.teamTableCell}>{member.email || "—"}</td>
      <td className={s.teamTableCell}>
        <span className={`${u.badge} ${roleBadgeClass(member.role)}`}>
          {roleLabel}
        </span>
      </td>
      <td className={s.teamTableCell}>
        <span className={s.teamTableMuted}>{detail}</span>
      </td>
      <td className={s.teamTableCell}>
        <span className={`${u.badge} ${status.tone === "ok" ? u.badgeGreen : u.badgeRed}`}>
          {status.label}
        </span>
      </td>
      <td className={`${s.teamTableCell} ${md.desktopTeamTableCellActions}`}>
        <TeamActions
          member={member}
          onResend={onResend}
          onToggleBlock={onToggleBlock}
          busyId={busyId}
          currentUserId={currentUserId}
          onProfile={onProfile}
          onSchedule={onSchedule}
          layout="table"
        />
      </td>
    </tr>
  );
}

export default function AdminTeamList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const isApp = useAdminHubUi();
  const currentUserId = useSelector((state) => state.auth.profile?.id);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [members, setMembers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [detailMember, setDetailMember] = useState(null);
  const [showInviteSheet, setShowInviteSheet] = useState(
    () => searchParams.get("invite") === "1",
  );
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("invite") !== "1") return;
    setShowInviteSheet(true);
    const next = new URLSearchParams(searchParams);
    next.delete("invite");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const load = useCallback(() => {
    teamAPI
      .list()
      .then((r) => setMembers(r.members || []))
      .catch((e) =>
        toast.error(e.response?.data?.error || "Не удалось загрузить команду"),
      );
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let list = members;
    if (roleFilter === "trainer") {
      list = list.filter((m) => m.role === "trainer");
    } else if (roleFilter === "admin") {
      list = list.filter((m) => m.role === "admin" || m.role === "owner");
    }
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(
      (m) =>
        (m.full_name || "").toLowerCase().includes(needle) ||
        (m.email || "").toLowerCase().includes(needle),
    );
  }, [members, q, roleFilter]);

  const onResend = async (userId) => {
    setBusyId(userId);
    try {
      const res = await teamAPI.resendInvite(userId);
      if (res?.result !== 0) {
        toast.error(res?.error || "Ошибка");
        return;
      }
      if (res.invite_url && !res.email_sent) {
        try {
          await navigator.clipboard.writeText(res.invite_url);
          toast.success("Ссылка скопирована (письмо не отправлено)", 6000);
        } catch {
          toast.success(res.message || "Ссылка готова", 6000);
        }
      } else {
        toast.success(res.message || "Приглашение отправлено");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setBusyId(null);
    }
  };

  const onToggleBlock = async (member) => {
    const next = !member.is_blocked;
    const name = member.full_name || member.email || "сотрудника";

    if (!next) {
      if (!window.confirm(`Разблокировать доступ для ${name}?`)) return;
    } else {
      let upcoming = 0;
      if (member.trainer_id) {
        try {
          upcoming = await loadUpcomingTrainerSessions(member.trainer_id);
        } catch {
          upcoming = 0;
        }
      }

      const lines = [
        `Заблокировать доступ для ${name}?`,
        "Сотрудник не сможет войти в приложение.",
      ];
      if (member.trainer_id) {
        lines.push(
          "Профиль тренера и занятия в календаре останутся — при необходимости сначала отмените или переназначьте тренировки.",
          "Чтобы скрыть тренера из записи без блокировки входа, откройте профиль и нажмите «Деактивировать тренера».",
        );
        if (upcoming > 0) {
          lines.push(
            `Сейчас у тренера ${upcoming} предстоящих занятий в ближайшие 3 месяца.`,
          );
        }
      }
      if (!window.confirm(lines.join("\n\n"))) return;
    }

    setBusyId(member.id);
    try {
      const res = await teamAPI.updateMember(member.id, { is_blocked: next });
      if (res?.result !== 0) {
        toast.error(res?.error || "Ошибка");
        return;
      }
      toast.success(res.message || "Готово");
      setDetailMember((prev) =>
        prev?.id === member.id ? { ...prev, is_blocked: next } : prev,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setBusyId(null);
    }
  };

  const openProfile = (trainerId) => {
    setDetailMember(null);
    navigate(`${DOCTORS_BASE}/${trainerId}`);
  };

  const openSchedule = (trainerId) => {
    setDetailMember(null);
    navigate(`/manage/app/schedule?trainer=${trainerId}&view=week`);
  };

  const openMember = (member) => {
    if (member.role === "trainer" && member.trainer_id) {
      openProfile(member.trainer_id);
      return;
    }
    setDetailMember(member);
  };

  const openMemberAccess = (member) => {
    setDetailMember(member);
  };

  const openInviteSheet = () => setShowInviteSheet(true);
  const closeInviteSheet = () => setShowInviteSheet(false);

  const handleInviteSuccess = (res) => {
    closeInviteSheet();
    load();
    const tid = res?.member?.trainer_id;
    if (tid) {
      navigate(`${DOCTORS_BASE}/${tid}`);
    }
  };

  const inviteSheetFooter = isApp ? (
    <HubSheetActions
      primary={inviteLoading ? "Отправка…" : "Пригласить"}
      primaryForm="team-invite-form"
      primaryDisabled={inviteLoading}
      onSecondary={closeInviteSheet}
    />
  ) : (
    <>
      <Button type="button" variant="secondary" onClick={closeInviteSheet}>
        Отмена
      </Button>
      <Button type="submit" form="team-invite-form" disabled={inviteLoading}>
        {inviteLoading ? "Отправка…" : "Пригласить"}
      </Button>
    </>
  );

  const renderInviteModal = () =>
    isApp ? (
      <HubSheet
        open={showInviteSheet}
        onClose={closeInviteSheet}
        title="Пригласить в команду"
        subtitle="Роль и группы — группу можно назначить позже"
        titleId="team-invite-title"
        footer={inviteSheetFooter}
        bodyClassName={aa.teamInviteSheetBody}
      >
        <TeamInviteForm
          sheet
          formId="team-invite-form"
          onLoadingChange={setInviteLoading}
          onSuccess={handleInviteSuccess}
          onCancel={closeInviteSheet}
        />
      </HubSheet>
    ) : (
      <DesktopDialog
        open={showInviteSheet}
        onClose={closeInviteSheet}
        title="Пригласить в команду"
        subtitle="Укажите данные тренера и при необходимости группы — группу можно добавить позже в профиле."
        titleId="team-invite-title"
        footer={inviteSheetFooter}
        wide
        bodyClassName={md.desktopTeamInviteDialogBody}
        dialogClassName={md.desktopTeamInviteDialog}
      >
        <TeamInviteForm
          sheet
          formId="team-invite-form"
          onLoadingChange={setInviteLoading}
          onSuccess={handleInviteSuccess}
        />
      </DesktopDialog>
    );

  const roleTabs = [
    { id: "", label: "Все" },
    { id: "admin", label: "Админы" },
    { id: "trainer", label: "Тренеры" },
  ];

  if (isApp) {
    return (
      <HubScreen
        title={L.team}
        right={<HubNavAdd onClick={openInviteSheet} ariaLabel="Пригласить" />}
      >
        <div className={aa.clientsSearchRow}>
          <label className={aa.clientsSearch}>
            <IconSearch className={aa.clientsSearchIcon} width={18} height={18} />
            <Input
              className={aa.clientsSearchInput}
              placeholder="ФИО или email"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Поиск сотрудников"
            />
          </label>
        </div>

        <HubSegment tabs={roleTabs} active={roleFilter} onChange={setRoleFilter} />

        {filtered.length > 0 ? (
          <p className={aa.hubCount}>{filtered.length} сотрудников</p>
        ) : null}

        {filtered.length === 0 ? (
          <EmptyState
            title={members.length ? "Никого не нашли" : "Пока только вы"}
            description={
              members.length
                ? "Смените фильтр или поиск"
                : "Пригласите первого сотрудника"
            }
            actionLabel={members.length ? undefined : "Пригласить"}
            onAction={members.length ? undefined : openInviteSheet}
          />
        ) : (
          <HubListGroup>
            {filtered.map((m) => {
              const status = memberStatus(m);
              const roleLabel = TEAM_ROLE_LABELS[m.role] || m.role;
              const detail = formatAssignments(m);
              const canManage = memberCanManage(m, currentUserId);
              const row = (
                <HubRow
                  title={m.full_name || m.email || "—"}
                  meta={`${roleLabel}${detail !== "—" ? ` · ${detail}` : ""}`}
                  sub={m.email || undefined}
                  badge={status.label}
                  badgeTone={status.tone}
                  onClick={() => openMember(m)}
                />
              );

              if (!canManage) {
                return <div key={m.id}>{row}</div>;
              }

              return (
                <SwipeableRow
                  key={m.id}
                  actions={[
                    {
                      id: "access",
                      label: "Доступ",
                      tone: "neutral",
                      onClick: () => openMemberAccess(m),
                    },
                  ]}
                >
                  {row}
                </SwipeableRow>
              );
            })}
          </HubListGroup>
        )}
        <HubSheet
          open={Boolean(detailMember)}
          onClose={() => setDetailMember(null)}
          title={detailMember?.full_name || detailMember?.email || "Сотрудник"}
          subtitle={
            detailMember
              ? TEAM_ROLE_LABELS[detailMember.role] || detailMember.role
              : undefined
          }
        >
          {detailMember ? (
            <>
              <div className={aa.teamMemberSheetHead}>
                {(() => {
                  const status = memberStatus(detailMember);
                  return (
                    <span
                      className={`${aa.listRowBadge} ${aa[`listRowBadge_${status.tone}`]}`}
                    >
                      {status.label}
                    </span>
                  );
                })()}
                {detailMember.email ? (
                  <p className={aa.teamMemberSheetEmail}>{detailMember.email}</p>
                ) : null}
                {formatAssignments(detailMember) !== "—" ? (
                  <p className={aa.teamMemberSheetDetail}>
                    {formatAssignments(detailMember)}
                  </p>
                ) : null}
              </div>
              {detailMember.role === "trainer" ? (
                <p className={aa.teamAccessHint}>
                  Блокировка закрывает вход в приложение, но не убирает занятия из
                  календаря. Перед блокировкой отмените или переназначьте тренировки в
                  расписании. Чтобы скрыть тренера из записи, в профиле нажмите
                  «Деактивировать тренера».
                </p>
              ) : null}
              <TeamActions
                member={detailMember}
                onResend={onResend}
                onToggleBlock={onToggleBlock}
                busyId={busyId}
                currentUserId={currentUserId}
                onProfile={openProfile}
                onSchedule={openSchedule}
              />
            </>
          ) : null}
        </HubSheet>
        {renderInviteModal()}
      </HubScreen>
    );
  }

  return (
    <div className={`${md.desktopHubPage} ${md.desktopTeamPage}`}>
      <header className={md.desktopHubPageHead}>
        <div className={md.desktopHubPageIntro}>
          <p className={md.desktopHubPageLead}>
            Приглашайте тренеров и назначайте группы. Тренерам доступен отдельный
            портал для журнала и расписания.
          </p>
          <p className={md.desktopHubPageMeta}>
            {filtered.length}{" "}
            {filtered.length === 1
              ? "сотрудник"
              : filtered.length < 5
                ? "сотрудника"
                : "сотрудников"}
          </p>
        </div>
        <Button size="sm" onClick={openInviteSheet}>
          <IconPlus width={16} height={16} />
          Пригласить
        </Button>
      </header>

      <div className={md.desktopHubPanel}>
        <div className={md.desktopTeamToolbar}>
          <label className={md.desktopTeamSearch}>
            <IconSearch className={md.desktopTeamSearchIcon} width={18} height={18} />
            <Input
              className={md.desktopTeamSearchInput}
              placeholder="ФИО или email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Поиск сотрудников"
            />
          </label>
          <Select
            className={md.desktopTeamRoleSelect}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            aria-label="Фильтр по роли"
          >
            <option value="">Все роли</option>
            <option value="admin">Администраторы</option>
            <option value="trainer">Тренеры</option>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            title={members.length ? "Никого не нашли" : "Пока только вы"}
            description={
              members.length
                ? "Смените фильтр или поиск"
                : "Пригласите первого тренера"
            }
            actionLabel={members.length ? undefined : "Пригласить"}
            onAction={members.length ? undefined : openInviteSheet}
          />
        ) : (
          <div className={`${s.teamTableWrap} ${md.desktopTeamTableWrap}`}>
            <table className={`${s.teamTable} ${md.desktopTeamTable}`}>
              <colgroup>
                <col />
                <col />
                <col />
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th>Email</th>
                  <th>Роль</th>
                  <th>Группы / залы</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <TeamTableRow
                    key={m.id}
                    member={m}
                    onResend={onResend}
                    onToggleBlock={onToggleBlock}
                    busyId={busyId}
                    currentUserId={currentUserId}
                    onProfile={openProfile}
                    onSchedule={openSchedule}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {renderInviteModal()}
    </div>
  );
}
